import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiBaseUrl, createApiHeaders } from '../../../../config/api';

/**
 * Hook for managing template data and loading.
 *
 * @param {string} templateId - Template ID to load
 * @param {string} tenant - Tenant identifier
 * @param {number} versionNumber - Optional version number to load specific version
 * @returns {Object} Template data, loading state, error, and load function
 */
export const useTemplateData = (templateId, tenant, versionNumber = null) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Keep previous template to avoid blank flash during version switch
  const [previousTemplate, setPreviousTemplate] = useState(null);
  // Use ref to track current template without causing re-renders
  const templateRef = useRef(null);

  // Update ref whenever template changes
  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  const loadTemplate = useCallback(async () => {
    if (!templateId) {
      setTemplate(null);
      setPreviousTemplate(null);
      templateRef.current = null;
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Keep previous template visible while loading new version
      // Capture current template from ref before starting load
      const currentTemplate = templateRef.current;
      if (currentTemplate) {
        setPreviousTemplate(currentTemplate);
      }
      const headers = createApiHeaders();

      // Build query parameters
      const params = new URLSearchParams();
      params.append('allow_draft', 'true');
      if (versionNumber !== null && versionNumber !== undefined) {
        params.append('version_number', versionNumber.toString());
      }

      // Try advanced endpoint first to support draft versions and specific versions
      let response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/advanced/${templateId}?${params.toString()}`,
        {
          headers,
        }
      );

      // If advanced endpoint doesn't exist (404), fallback to basic endpoint with status=all
      if (!response.ok && response.status === 404) {
        const fallbackParams = new URLSearchParams();
        fallbackParams.append('status', 'all');
        if (versionNumber !== null && versionNumber !== undefined) {
          fallbackParams.append('version', versionNumber.toString());
        }
        response = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/${templateId}?${fallbackParams.toString()}`,
          {
            headers,
          }
        );
      }

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `Failed to load template: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setTemplate(data);
      templateRef.current = data;
      // Clear previous template once new one is loaded
      setPreviousTemplate(null);
    } catch (err) {
      setError(err.message);
      // On error, keep previous template visible if available
      // previousTemplate is already set above if currentTemplate existed
    } finally {
      setLoading(false);
    }
  }, [templateId, versionNumber]);

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
    // Note: loadTemplate already includes versionNumber in its dependencies,
    // so it will automatically reload when versionNumber changes
  }, [templateId, loadTemplate]);

  // Return previous template if current one is loading (stale-while-revalidate pattern)
  const displayTemplate =
    loading && previousTemplate ? previousTemplate : template;

  return {
    template: displayTemplate,
    loading,
    error,
    loadTemplate,
    isStale: loading && previousTemplate !== null, // Indicates we're showing stale data
  };
};
